'use client';

import { useCallback, useEffect, useRef } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';

import type { Drug } from '../components/drug-search';
import {
  BAZA_LEKOW_PREFIX,
  type DrugRowForSlug,
  buildBazaLekowUrl,
} from '../lib/slug';

const DRUGS_PARAM = 'leki';

interface DrugMetadata {
  id: string;
  productName: string;
  commonName: string;
  strength: string;
  form: string;
  hasChplPdf?: boolean;
  hasUlotkaPdf?: boolean;
  hasChplExtraction?: boolean;
}

interface UseUrlDrugSyncOptions {
  onDrugsFromUrl?: (drugs: Drug[]) => void;
  /** Called when 2+ drugs are loaded from URL — use to probe cache */
  onAutoCheck?: () => void;
  onInitialized?: () => void;
  initialDrugs?: Drug[];
}

interface UseUrlDrugSyncResult {
  syncDrugsToUrl: (drugs: Drug[]) => void;
  getShareableUrl: (drugs: Drug[]) => string;
}

function isBazaLekowPath(pathname: string): boolean {
  return (
    pathname === BAZA_LEKOW_PREFIX ||
    pathname.startsWith(`${BAZA_LEKOW_PREFIX}/`)
  );
}

function shouldUseSeoPathUrls(pathname: string): boolean {
  return isBazaLekowPath(pathname) || pathname === '/' || pathname === '/home';
}

function drugToRow(drug: Drug): DrugRowForSlug {
  return {
    id: drug.id,
    product_name: drug.productName,
    common_name: drug.commonName || null,
    strength: drug.strength || null,
    form: drug.form || null,
  };
}

/**
 * Extract drug IDs from /baza-lekow path segments.
 * Handles:
 * - /baza-lekow/{slug}-{id}                       (single drug)
 * - /baza-lekow/chpl/{slug}-{id}                  (single drug ChPL)
 * - /baza-lekow/ulotka/{slug}-{id}                (single drug Ulotka)
 * - /baza-lekow/interakcje/{slug1}-{id1}/{slug2}-{id2}/...  (multi-drug)
 */
function extractDrugIdsFromPath(pathname: string): string[] {
  const rest = pathname.slice(BAZA_LEKOW_PREFIX.length + 1);
  if (!rest) return [];

  const segments = rest.split('/').filter(Boolean);
  if (segments.length === 0) return [];

  const first = segments[0]!;
  const drugSegments =
    first === 'chpl' || first === 'ulotka' || first === 'interakcje'
      ? segments.slice(1)
      : segments;

  return drugSegments
    .map((s) => (/^\d+$/.test(s) ? s : s.match(/-(\d+)$/)?.[1]))
    .filter((id): id is string => id !== undefined);
}

/**
 * Hook for syncing drug selection to URL.
 * When 2+ drugs are loaded from URL, automatically triggers interaction check.
 *
 * On /baza-lekow routes: uses path-based URLs
 * On legacy routes: uses query params (?leki=id1,id2)
 */
export function useUrlDrugSync(
  options: UseUrlDrugSyncOptions = {},
): UseUrlDrugSyncResult {
  const { onDrugsFromUrl, onAutoCheck, onInitialized, initialDrugs } = options;
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const hasProcessedUrl = useRef(false);
  const drugsCache = useRef<Map<string, DrugMetadata>>(new Map());

  const useSeoPathUrls = shouldUseSeoPathUrls(pathname);
  const isBazaLekow = isBazaLekowPath(pathname);

  const loadDrugMetadata = useCallback(async (): Promise<
    Map<string, DrugMetadata>
  > => {
    if (drugsCache.current.size > 0) {
      return drugsCache.current;
    }

    try {
      const res = await fetch('/data/chpl_metadata/drugs.json', {
        cache: 'no-store',
      });
      const all = (await res.json()) as DrugMetadata[];
      const map = new Map<string, DrugMetadata>();
      for (const drug of all) {
        map.set(drug.id, drug);
      }
      drugsCache.current = map;
      return map;
    } catch {
      console.error('Failed to load drug metadata');
      return new Map();
    }
  }, []);

  useEffect(() => {
    if (hasProcessedUrl.current) {
      return;
    }
    hasProcessedUrl.current = true;

    let drugIds: string[] = [];

    if (isBazaLekow) {
      drugIds = extractDrugIdsFromPath(pathname);
    } else {
      const drugsParam = searchParams.get(DRUGS_PARAM);
      drugIds = drugsParam?.split(',').filter(Boolean) ?? [];
    }

    if (drugIds.length === 0) {
      onInitialized?.();
      return;
    }

    const initialDrugIds = initialDrugs?.map((d) => d.id) ?? [];
    const drugsAlreadyLoaded =
      initialDrugs &&
      initialDrugs.length > 0 &&
      drugIds.every((id) => initialDrugIds.includes(id)) &&
      initialDrugIds.every((id) => drugIds.includes(id));

    const notifyAndInit = (drugs: Drug[]) => {
      if (drugs.length >= 2) {
        onAutoCheck?.();
      }
      onInitialized?.();
    };

    if (drugsAlreadyLoaded) {
      notifyAndInit(initialDrugs);
      return;
    }

    void (async () => {
      const metadata = await loadDrugMetadata();
      const drugs: Drug[] = [];

      for (const id of drugIds) {
        const drug = metadata.get(id);
        if (drug) {
          drugs.push({
            id: drug.id,
            productName: drug.productName,
            commonName: drug.commonName,
            strength: drug.strength,
            form: drug.form,
            hasChplPdf: drug.hasChplPdf,
            hasUlotkaPdf: drug.hasUlotkaPdf,
            hasChplExtraction: drug.hasChplExtraction,
          });
        }
      }

      if (drugs.length > 0) {
        onDrugsFromUrl?.(drugs);
      }

      notifyAndInit(drugs);
    })();
  }, [
    searchParams,
    loadDrugMetadata,
    onDrugsFromUrl,
    onAutoCheck,
    onInitialized,
    initialDrugs,
    pathname,
    useSeoPathUrls,
    isBazaLekow,
  ]);

  const syncDrugsToUrl = useCallback(
    (drugs: Drug[]) => {
      if (useSeoPathUrls) {
        const rows = drugs.map(drugToRow);
        const newPath = buildBazaLekowUrl(rows);
        window.history.replaceState({}, '', newPath);
      } else {
        const leki = drugs.length > 0 ? drugs.map((d) => d.id).join(',') : null;
        const parts: string[] = [];
        if (leki) parts.push(`${DRUGS_PARAM}=${leki}`);
        const newUrl =
          parts.length > 0 ? `${pathname}?${parts.join('&')}` : pathname;
        window.history.replaceState({}, '', newUrl);
      }
    },
    [pathname, useSeoPathUrls],
  );

  const getShareableUrl = useCallback(
    (drugs: Drug[]): string => {
      const origin =
        typeof window !== 'undefined' ? window.location.origin : '';

      if (useSeoPathUrls) {
        const rows = drugs.map(drugToRow);
        const path = buildBazaLekowUrl(rows);
        return `${origin}${path}`;
      }

      const leki = drugs.length > 0 ? drugs.map((d) => d.id).join(',') : null;
      const parts: string[] = [];
      if (leki) parts.push(`${DRUGS_PARAM}=${leki}`);
      const base = origin + pathname;
      return parts.length > 0 ? `${base}?${parts.join('&')}` : base;
    },
    [pathname, useSeoPathUrls],
  );

  return {
    syncDrugsToUrl,
    getShareableUrl,
  };
}
